// client/src/services/biometricAuth.ts — OTT 2026
//
// Sblocco con impronta/Face ID (richiesta owner): dopo il PRIMO login
// completo (password + eventuale 2FA), l'utente attiva lo sblocco su questo
// device. Alle riaperture successive la biometria sblocca una sessione
// nuova, senza ripetere password/2FA.
//
// Architettura:
//   - il device riceve dal server un SECRET (256 bit) restituito una sola
//     volta e custodito nel secure storage (Keystore Android / Keychain iOS)
//   - il segreto vive SOLO sul device; in DB ne esiste solo l'hash bcrypt
//   - la biometria è il gate LOCALE che precede la lettura del segreto
//   - se il server invalida il device (revoca/TTL 180 giorni) → BIOMETRIC_INVALID
//     e si torna al login completo
//
// Nota: su browser/desktop la biometria non è disponibile → le funzioni
// `enable`/`tryLogin` rispondono "unavailable" e l'UI non mostra l'opzione.

import { Capacitor } from "@capacitor/core";
import { BiometricAuth, BiometryType } from "@aparajita/capacitor-biometric-auth";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";
import { Preferences } from "@capacitor/preferences";

import {
  enrollBiometricDevice,
  biometricRefresh,
  revokeBiometricDevice,
} from "@/api";
import type { User } from "@/types";

const DEVICE_ID_KEY = "rc_biometric_device_id";
const SECRET_KEY = "rc_biometric_secret";
const ENABLED_KEY = "rc_biometric_enabled";

/** true solo nell'app nativa (Android/iOS): la biometria non esiste sul web. */
export function isBiometricSupported(): boolean {
  return Capacitor.isNativePlatform();
}

function randomDeviceId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fallback sotto */
  }
  return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

async function getOrCreateDeviceId(): Promise<string> {
  const { value } = await Preferences.get({ key: DEVICE_ID_KEY });
  if (value) return value;
  const id = randomDeviceId();
  await Preferences.set({ key: DEVICE_ID_KEY, value: id });
  return id;
}

/** Etichetta leggibile per il tipo di biometria del device (enum numerico). */
function labelForBiometryType(type: BiometryType): string {
  switch (type) {
    case BiometryType.faceId:
      return "Face ID";
    case BiometryType.touchId:
      return "Touch ID";
    case BiometryType.fingerprintAuthentication:
      return "Impronta digitale";
    case BiometryType.faceAuthentication:
      return "Riconoscimento del volto";
    case BiometryType.irisAuthentication:
      return "Iris";
    default:
      return "Biometria";
  }
}

/** Disponibilità della biometria sul device (sensore enrolato incluso). */
export async function getBiometricAvailability(): Promise<{
  available: boolean;
  label: string;
}> {
  if (!isBiometricSupported()) return { available: false, label: "" };
  try {
    const info = await BiometricAuth.checkBiometry();
    return {
      available: !!info.isAvailable,
      label: labelForBiometryType(info.biometryType),
    };
  } catch {
    return { available: false, label: "" };
  }
}

async function authenticateBiometric(reason: string): Promise<boolean> {
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: "Annulla",
      allowDeviceCredential: false,
    });
    return true;
  } catch {
    return false;
  }
}

/** true se l'utente ha attivato lo sblocco biometrico su QUESTO device. */
export async function isBiometricUnlockEnabled(): Promise<boolean> {
  if (!isBiometricSupported()) return false;
  const { value } = await Preferences.get({ key: ENABLED_KEY });
  return value === "true";
}

export interface EnableResult {
  ok: boolean;
  error?: string;
}

/**
 * Attiva lo sblocco: biometria → enroll sul server → secret nel secure storage.
 * Richiede una sessione valida (l'utente sta configurando il device).
 */
export async function enableBiometricUnlock(): Promise<EnableResult> {
  if (!isBiometricSupported()) {
    return { ok: false, error: "Disponibile solo nell'app per Android/iOS." };
  }
  const { available, label } = await getBiometricAvailability();
  if (!available) {
    return {
      ok: false,
      error:
        "Biometria non disponibile su questo dispositivo. Configurala nelle impostazioni del telefono e riprova.",
    };
  }

  const authOk = await authenticateBiometric(
    "Attiva lo sblocco con la biometria per RideConnect",
  );
  if (!authOk) return { ok: false, error: "Autenticazione annullata." };

  try {
    const deviceId = await getOrCreateDeviceId();
    const res = await enrollBiometricDevice(
      deviceId,
      `${Capacitor.getPlatform() === "ios" ? "iPhone/iPad" : "Android"} · ${label}`,
    );
    await SecureStoragePlugin.set({ key: SECRET_KEY, value: res.device_secret });
    await Preferences.set({ key: ENABLED_KEY, value: "true" });
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e?.message || "Attivazione non riuscita." };
  }
}

/** Disattiva lo sblocco: revoca sul server + pulizia locale (best-effort). */
export async function disableBiometricUnlock(): Promise<void> {
  try {
    const { value: deviceId } = await Preferences.get({ key: DEVICE_ID_KEY });
    if (deviceId) {
      await revokeBiometricDevice(deviceId).catch(() => {});
    }
  } finally {
    await SecureStoragePlugin.remove({ key: SECRET_KEY }).catch(() => {});
    await Preferences.remove({ key: ENABLED_KEY });
  }
}

/**
 * Pulisce SOLO lo stato locale (server già invalidato, es. device revocato
 * da un altro device): la funzione risulta disattivata su questo telefono.
 */
async function clearLocalBiometricState(): Promise<void> {
  await SecureStoragePlugin.remove({ key: SECRET_KEY }).catch(() => {});
  await Preferences.remove({ key: ENABLED_KEY });
}

export type BiometricLoginResult =
  | { status: "ok"; user: User; accessToken: string }
  | { status: "cancelled" }
  | { status: "invalid" }
  | { status: "conflict" }
  | { status: "unavailable" };

/**
 * Flusso di sblocco: biometria → secret dal secure storage → refresh sul
 * server → sessione. Da chiamare SOLO quando l'utente non è autenticato.
 */
export async function tryBiometricLogin(): Promise<BiometricLoginResult> {
  if (!isBiometricSupported()) return { status: "unavailable" };
  if (!(await isBiometricUnlockEnabled())) return { status: "unavailable" };

  const { available } = await getBiometricAvailability();
  if (!available) return { status: "unavailable" };

  const authOk = await authenticateBiometric("Sblocca RideConnect");
  if (!authOk) return { status: "cancelled" };

  try {
    const { value: deviceId } = await Preferences.get({ key: DEVICE_ID_KEY });
    let secret: string | null = null;
    try {
      const r = await SecureStoragePlugin.get({ key: SECRET_KEY });
      secret = r?.value ?? null;
    } catch {
      secret = null;
    }
    if (!deviceId || !secret) {
      await clearLocalBiometricState();
      return { status: "unavailable" };
    }

    const data = await biometricRefresh(deviceId, secret);
    return { status: "ok", user: data.user, accessToken: data.accessToken };
  } catch (e: any) {
    // Device revocato/scaduto lato server: pulizia locale e login normale.
    if (e?.data?.code === "BIOMETRIC_INVALID" || e?.status === 401) {
      await clearLocalBiometricState();
      return { status: "invalid" };
    }
    // SESSIONE UNICA (Ott 2026): esiste già un'altra sessione attiva —
    // il gate mostra il messaggio e rimanda al login con password.
    if (e?.data?.code === "SESSION_CONFLICT") {
      return { status: "conflict" };
    }
    return { status: "cancelled" };
  }
}
